import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { PushMessageService } from "@obg/mat.ui.common";
import { ActionWithPayload } from "@obg/ngrx-utils";
import {
	AppContextSelector,
	AUTH_ACTION_TYPE,
	AuthSelector,
	COMMAND_DISPATCHER_ACTION_TYPE,
	CommandType,
	CORE_ROUTE,
	CoreState,
	ProductType,
	SessionStartPayload,
} from "@obg/ngx.core";
import { DocumentRef, PlatformService, SportsbookAppValue, WindowRef } from "@obg/ngx.global.evo";
import { SignalrAction } from "@obg/ngx.real-time.signalr";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { ScrollToTopService } from "@obg/ngx.ui.global.scrollbar";
import { ignoreNil } from "@obg/rxjs";
import { ReplaySubject } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { AppBootstrapper } from "./app.bootstrap";
import { AppMessagesBootstrap } from "./app.messages.bootstrap";

@Injectable({
	providedIn: "root",
})
export class AppEffect {
	private initialized = false;
	private exposedIsAuthenticated$ = this.platformService.isBrowser
		? this.documentRef.obgEventBus.expose(SportsbookAppValue.isUserAuthenticated)
		: new ReplaySubject<boolean>(1);
	private exposedIsProductSportsbook$ = this.platformService.isBrowser
		? this.documentRef.obgEventBus.expose(SportsbookAppValue.isProductSportsbook)
		: new ReplaySubject<boolean>(1);

	bootstrap$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RouteAction.navigationStart),
				take(1),
				filter(() => !this.initialized),
				tap(() => {
					this.appBootstrapper.init();
					this.appMessagesBootstrap.init();

					this.initialized = true;
				})
			),
		{ dispatch: false }
	);

	goToTopCommand$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(COMMAND_DISPATCHER_ACTION_TYPE.trigger),
				map((x: ActionWithPayload<CommandType>) => x.payload),
				filter((commandType) => commandType === CommandType.BackToTop),
				tap(() => {
					this.scrollToTopService.triggerCommandScroll();
					// this is required when not using custom scroll
					this.windowRef.native.scrollTo(0, 0);
				})
			),
		{ dispatch: false }
	);

	openCookieSettings$ = createEffect(() =>
		this.actions$.pipe(
			ofType(COMMAND_DISPATCHER_ACTION_TYPE.trigger),
			map((x: ActionWithPayload<CommandType>) => x.payload),
			filter((commandType) => commandType === CommandType.OpenCookieSettings),
			map(() => RouteAction.navigate(CORE_ROUTE.cookieSettings))
		)
	);

	resolveEnd$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RouteAction.resolveEnd),
				switchMap(() => this.store.select(RouteSelector.getCurrent)),
				ignoreNil(),
				tap((route) => this.pushMessageService.initialize(route.name))
			),
		{ dispatch: false }
	);

	connectRealtime$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionStart),
			map((x: ActionWithPayload<SessionStartPayload>) => x.payload),
			map(({ token }) => SignalrAction.connectCustomerTransmit(token))
		)
	);

	connectCustomerTransmitKeepAliveCheck$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.keepAlive),
			switchMap(() => this.store.select(AuthSelector.getSessionToken)),
			ignoreNil(),
			map((token) => SignalrAction.connectCustomerTransmit(token))
		)
	);

	disconnectRealtime$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.logout, AUTH_ACTION_TYPE.sessionEnd),
			map(() => SignalrAction.disconnectCustomerTransmit())
		)
	);

	sportsbookIsAuthenticated$ = this.platformService.forClient(() =>
		createEffect(
			() =>
				this.store.pipe(
					select(AuthSelector.isAuthenticated),
					tap((isAuthenticated) => this.exposedIsAuthenticated$.next(isAuthenticated))
				),
			{ dispatch: false }
		)
	);

	isProductSportsbookB2c$ = this.platformService.forClient(() =>
		createEffect(
			() =>
				this.store.pipe(
					select(AppContextSelector.getProduct),
					tap((product) => this.exposedIsProductSportsbook$.next(product === ProductType.Sportsbook))
				),
			{ dispatch: false }
		)
	);

	constructor(
		private actions$: Actions,
		private scrollToTopService: ScrollToTopService,
		private pushMessageService: PushMessageService,
		private windowRef: WindowRef,
		private documentRef: DocumentRef,
		private platformService: PlatformService,
		private appMessagesBootstrap: AppMessagesBootstrap,
		private appBootstrapper: AppBootstrapper,
		private store: Store<CoreState>
	) {}
}
